import { useState, useEffect } from "react";
import { Crown, Edit3, Package, ShoppingCart, Settings, Store, Shield, ArrowRight, X } from "lucide-react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import TopHeader from "../components/TopHeader";
import FeedControls from "../components/FeedControls";
import ReviewFeed from "../components/ReviewFeed";
import CreateReviewModal from "../components/CreateReviewModal";
import CameraFirstReview from "../components/CameraFirstReview";
import OrderInitiationModal from "../components/OrderInitiationModal";
import RestaurantList from "./RestaurantList";
import LocationPrompt from "../components/LocationPrompt";
import DraftReviewsManager from "../components/DraftReviewsManager";
import PostOrderReviewPrompt from "../components/PostOrderReviewPrompt";
import FriendsActivity from "../components/FriendsActivity";
import CommentsManager from "../components/CommentsManager";
import { locationService } from "../services/LocationService";
import { useUserProfileOptional } from "@/hooks/useUserProfile";
import { useSessionLocation } from "@/hooks/useSessionLocation";
import { Link } from "wouter";
import TrendingPanel from "@/components/TrendingPanel";
import { SectionErrorBoundary } from "@/components/AppErrorBoundary";
import { type OrderHistoryItem } from "@/hooks/useOrderHistory";
import { usePermissions } from "@/hooks/usePermissions";
import { RoleType } from "@shared/permissions";
import { Button } from "@/components/ui/button";
import AuthPromptBanner from "@/components/AuthPromptBanner";
import { useAuth } from "@/hooks/useAuth";

export default function Home() {
  const [location, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState('feed');
  const [showCommentsManager, setShowCommentsManager] = useState(false);
  const [portalBannerDismissed, setPortalBannerDismissed] = useState(() => {
    return sessionStorage.getItem('portalBannerDismissed') === 'true';
  });
  
  const userProfile = useUserProfileOptional();
  const { isAuthenticated } = useAuth();
  const { hasRole } = usePermissions();
  const { data: roleData } = useQuery<{ role: string }>({
    queryKey: ['/api/user/role'],
    enabled: isAuthenticated,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const isAdmin = roleData?.role === 'admin' || roleData?.role === 'super_admin';
  const showPartner = hasRole(RoleType.RestaurantManager);
  const showPortalBanner = !portalBannerDismissed && (showPartner || isAdmin);

  const dismissPortalBanner = () => {
    setPortalBannerDismissed(true);
    sessionStorage.setItem('portalBannerDismissed', 'true');
  };
  
  // Parse URL parameters for filtering
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const restaurant = params.get('restaurant');
    
    if (tab) {
      setActiveTab(tab);
    }
    if (restaurant) {
      setFeedSearchQuery(restaurant);
    }
  }, []); // Removed location dependency to prevent infinite loop
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCameraFirstReview, setShowCameraFirstReview] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrderForReview, setSelectedOrderForReview] = useState<OrderHistoryItem | null>(null);
  const [orderParams, setOrderParams] = useState<{
    serviceType: 'delivery' | 'takeout';
    timing: 'asap' | 'scheduled';
    scheduledTime?: string;
    location: string;
    radius?: number;
  } | null>(null);
  const [cartTotal, setCartTotal] = useState(0);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({ cuisine: [] as string[], location: [] as string[], price: [] as string[] });
  
  const { selectedLocationName, setSessionLocation } = useSessionLocation();
  
  const currentLocation = selectedLocationName
    || (userProfile?.primaryAddress
      ? (userProfile.primaryAddress.label || `${userProfile.primaryAddress.streetAddress}, ${userProfile.primaryAddress.city}`)
      : "Select location");
  const [activeFilters, setActiveFilters] = useState<{ include: string[]; exclude: string[] }>({ include: [], exclude: [] });
  const [feedSearchQuery, setFeedSearchQuery] = useState("");
  const [widgetFilter, setWidgetFilter] = useState<string | null>(null);
  const [selectedExperienceTypes, setSelectedExperienceTypes] = useState<('dine-in' | 'takeout' | 'delivery')[]>(['dine-in', 'takeout', 'delivery']);
  const [selectedDistance, setSelectedDistance] = useState<number | null>(5);
  
  // Location-based review prompts
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [locationPromptData, setLocationPromptData] = useState<{
    restaurant: any;
    timeSpent: number;
    sessionId: string;
  } | null>(null);
  const [locationPermissionRequested, setLocationPermissionRequested] = useState(false);
  
  // Review management integration
  const [showDraftManager, setShowDraftManager] = useState(false);
  const [showPostOrderPrompt, setShowPostOrderPrompt] = useState(false);
  const [postOrderData, setPostOrderData] = useState<any>(null);
  
  // Get real user data from profile context
  const realUser = userProfile ? {
    id: String(userProfile.profile?.userId || 'guest'),
    name: userProfile.profile?.name || userProfile.profile?.firstName || 'Guest',
    kudozBalance: userProfile.kudozBalance || 0,
    avatar: userProfile.profile?.profilePicture,
  } : null;

  // Fetch unread message count (comments on user's reviews + mentions)
  const { data: unreadMessageData } = useQuery<{ count: number }>({
    queryKey: ['/api/user/unread-messages-count'],
    enabled: !!realUser,
    refetchInterval: 60000, // Refresh every minute
  });

  // Mock post-order data for integration demo
  const mockPostOrderData = {
    id: "order_demo_123",
    restaurantId: "rest_demo_1",
    restaurantName: "Demo Pizza Palace",
    type: 'delivery' as const,
    status: 'delivered' as const,
    items: [
      {
        itemId: "item_1",
        itemName: "Margherita Pizza",
        quantity: 1,
        price: 18.99,
        specialInstructions: "Extra cheese please"
      },
      {
        itemId: "item_2",
        itemName: "Caesar Salad",
        quantity: 1,
        price: 12.99
      }
    ],
    subtotal: 31.98,
    tax: 2.88,
    tip: 5.00,
    total: 39.86,
    deliveryAddress: "123 Main St, NYC",
    estimatedDeliveryTime: new Date(Date.now() - 35 * 60 * 1000).toISOString(), // 35 min ago
    actualDeliveryTime: new Date(Date.now() - 25 * 60 * 1000).toISOString(), // 25 min ago
    createdAt: new Date(Date.now() - 65 * 60 * 1000).toISOString() // 65 min ago
  };

  const handleTabChange = (tab: string) => {
    if (tab === 'profile') {
      // Navigate to full profile page instead of showing inline tab
      setLocation('/profile');
    } else {
      setActiveTab(tab);
    }
  };

  const handleCreateClick = () => {
    // Go directly to camera-first review, bypassing the type selection modal
    // "From Past Order" reviews are accessible from the Profile page
    setSelectedOrderForReview(null);
    setShowCameraFirstReview(true);
  };

  // Listen for global "create review" events from ConsumerLayout's nav buttons
  useEffect(() => {
    const handler = () => handleCreateClick();
    window.addEventListener('kudoz:open-create-review', handler);
    return () => window.removeEventListener('kudoz:open-create-review', handler);
  }, []);

  const handleDineInReview = () => {
    setSelectedOrderForReview(null);
    setShowCameraFirstReview(true);
  };

  // Mock order data mapping
  const mockOrders: { [key: string]: any } = {
    'order_1': {
      id: 'order_1',
      restaurantId: 'rest_1',
      restaurantName: 'Artisan Pizza Co',
      type: 'delivery' as const,
      items: [
        { itemId: 'item_1', itemName: 'Margherita Pizza', quantity: 1, price: 18.00 },
        { itemId: 'item_2', itemName: 'Caesar Salad', quantity: 1, price: 12.50 }
      ]
    },
    'order_2': {
      id: 'order_2',
      restaurantId: 'rest_2',
      restaurantName: 'Sushi Harbor',
      type: 'takeout' as const,
      items: [
        { itemId: 'item_3', itemName: 'Rainbow Roll', quantity: 1, price: 16.00 },
        { itemId: 'item_4', itemName: 'Spicy Tuna', quantity: 1, price: 14.00 },
        { itemId: 'item_5', itemName: 'Miso Soup', quantity: 2, price: 6.00 }
      ]
    },
    'order_3': {
      id: 'order_3',
      restaurantId: 'rest_3',
      restaurantName: 'Thai Spice Garden',
      type: 'delivery' as const,
      items: [
        { itemId: 'item_6', itemName: 'Pad Thai', quantity: 1, price: 15.99 },
        { itemId: 'item_7', itemName: 'Tom Yum Soup', quantity: 1, price: 8.00 }
      ]
    }
  };

  const handleOrderReview = (orderId: string, orderData?: OrderHistoryItem) => {
    setSelectedOrderForReview(orderData || null);
    setShowCreateModal(true);
  };

  const handleOrderProceed = (params: {
    serviceType: 'delivery' | 'takeout';
    timing: 'asap' | 'scheduled';
    scheduledTime?: string;
    location: string;
    radius?: number;
  }) => {
    setOrderParams(params);
    setActiveTab('menus');
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    console.log('Searching for:', query);
  };

  const handleFilterChange = (newFilters: { cuisine: string[], location: string[], price: string[] }) => {
    setFilters(newFilters);
    console.log('Filters applied:', newFilters);
  };

  // Initialize location monitoring
  useEffect(() => {
    const initializeLocation = async () => {
      // Request location permission and start monitoring
      if (!locationPermissionRequested) {
        setLocationPermissionRequested(true);
        
        // Set up location service callbacks
        locationService.onReviewPrompt((data) => {
          setLocationPromptData(data);
          setShowLocationPrompt(true);
        });
        
        // Start location monitoring with real user ID
        const success = await locationService.startLocationMonitoring(realUser?.id?.toString() || 'guest-user');
        
        if (success) {
          console.log('Location monitoring started successfully');
        } else {
          console.log('Location monitoring failed or permission denied');
        }
      }
    };
    
    initializeLocation();
    
    // Cleanup on unmount
    return () => {
      locationService.stopLocationMonitoring();
    };
  }, [locationPermissionRequested, realUser?.id]);

  const handleStartReview = (restaurantId: string, sessionId: string) => {
    setShowLocationPrompt(false);
    setShowCreateModal(true);
    
    // Mark review as created in location service
    locationService.markReviewCreated();
    
    console.log('Starting review for restaurant:', restaurantId, 'session:', sessionId);
  };

  const handleDismissLocationPrompt = () => {
    setShowLocationPrompt(false);
    setLocationPromptData(null);
  };

  // Scroll-based controls visibility
  const [showControls, setShowControls] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrollContainerRef, setScrollContainerRef] = useState<HTMLDivElement | null>(null);

  // Integration handlers
  const handleShowDraftManager = () => {
    setShowDraftManager(true);
  };

  const handleShowPostOrderDemo = () => {
    setPostOrderData(mockPostOrderData);
    setShowPostOrderPrompt(true);
  };

  const handleReviewSubmitted = (reviewData: any) => {
    console.log('Review submitted to feed integration:', reviewData);
    // In a real app, this would update the feed or trigger a refresh
  };

  const handleLocationChange = (name: string, lat?: number, lng?: number) => {
    setSessionLocation(name, lat, lng);
    setWidgetFilter(null);
  };

  const handleFiltersChange = (filters: { include: string[]; exclude: string[] }) => {
    setActiveFilters(filters);
    
    // Map new tag-based filters to legacy format for ReviewFeed
    // For now, we'll just use the include filters as a simple mapping
    const legacyFilters = {
      cuisine: filters.include.filter(tag => ['italian', 'mexican', 'asian', 'american', 'seafood', 'vegetarian', 'vegan', 'thai', 'indian', 'chinese', 'mediterranean', 'bbq', 'fusion', 'southern', 'japanese'].includes(tag)),
      location: filters.include.filter(tag => ['fast-delivery', 'on-time', 'quick-prep'].includes(tag)),
      price: filters.include
        .filter(tag => ['great-value', 'cheap-eats', 'budget-friendly', 'affordable', 'low-fees'].includes(tag))
        .map(tag => {
          // Map price tags to legacy format
          if (['great-value', 'cheap-eats', 'budget-friendly', 'affordable', 'low-fees'].includes(tag)) {
            return '$';
          }
          return tag;
        })
    };
    setFilters(legacyFilters);
    
    console.log('Filters applied:', filters, 'Legacy mapped:', legacyFilters);
  };

  const handleFeedSearchChange = (query: string) => {
    setFeedSearchQuery(query);
    setWidgetFilter(null);
    console.log('Feed search query:', query);
  };

  const handleExperienceTypeToggle = (type: 'dine-in' | 'takeout' | 'delivery') => {
    setSelectedExperienceTypes(prev => {
      if (prev.includes(type)) {
        // Don't allow deselecting all types - keep at least one
        if (prev.length === 1) return prev;
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
    console.log('Experience types toggled:', type);
  };

  const handleDistanceChange = (distance: number | null) => {
    setSelectedDistance(distance);
    console.log('Distance changed to:', distance);
  };

  // Handle scroll for controls visibility
  useEffect(() => {
    if (!scrollContainerRef) return;

    const handleScroll = () => {
      const currentScrollY = scrollContainerRef.scrollTop;
      
      // Only hide if scrolled down more than 50px
      if (currentScrollY < 50) {
        setShowControls(true);
      } else if (currentScrollY > lastScrollY) {
        // Scrolling down - hide controls
        setShowControls(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up - show controls
        setShowControls(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    scrollContainerRef.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      scrollContainerRef.removeEventListener('scroll', handleScroll);
    };
  }, [scrollContainerRef, lastScrollY]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'feed':
        return (
          <div className="flex-1 flex flex-col overflow-hidden relative">
            <div 
              className="sticky top-0 z-40 bg-background overflow-hidden transition-all duration-300 ease-in-out"
              style={{
                maxHeight: showControls ? '500px' : '0px',
                opacity: showControls ? 1 : 0
              }}
            >
              <FeedControls
                selectedLocation={currentLocation}
                activeFilters={activeFilters}
                searchQuery={feedSearchQuery}
                selectedExperienceTypes={selectedExperienceTypes}
                selectedDistance={selectedDistance}
                onLocationChange={handleLocationChange}
                onFiltersChange={handleFiltersChange}
                onSearchChange={handleFeedSearchChange}
                onExperienceTypeToggle={handleExperienceTypeToggle}
                onDistanceChange={handleDistanceChange}
                onOrderNowClick={() => setShowOrderModal(true)}
                cartTotal={cartTotal}
                cartItemCount={cartItemCount}
              />
            </div>
            {widgetFilter && (
              <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 border-b border-border">
                <span className="text-xs text-primary font-medium flex-1 truncate">
                  Prioritising: <span className="font-semibold">{widgetFilter}</span>
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                  onClick={() => setWidgetFilter(null)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}
            <div 
              ref={setScrollContainerRef}
              className="flex-1 overflow-y-auto"
            >
              <SectionErrorBoundary section="ReviewFeed">
                <ReviewFeed 
                  filter="all" 
                  searchQuery={feedSearchQuery} 
                  filters={filters}
                  selectedExperienceTypes={selectedExperienceTypes}
                  highlightTags={activeFilters.include.filter(tag => 
                    ['the-best', 'elite-reviewers', 'trusted-foodie-picks', 'top-rated-9plus', 
                     'friends-recommend', 'following', 'high-trust-score', 'trending-now'].includes(tag)
                  )}
                  priorityQuery={widgetFilter ?? undefined}
                />
              </SectionErrorBoundary>
            </div>
          </div>
        );
      case 'menus':
        return (
          <RestaurantList embedded={true} />
        );
      case 'friends':
        return <FriendsActivity />;
      default:
        return <ReviewFeed filter="all" />;
    }
  };

  return (
    <div className="flex flex-col h-full min-w-0 overflow-hidden" data-testid="home-page">
      <TopHeader
        user={realUser || undefined}
        unreadAlertCount={userProfile?.unreadAlertCount || 0}
        unreadMessageCount={unreadMessageData?.count || 0}
        onNotificationsClick={() => setLocation('/profile?section=alerts')}
        onMessagesClick={() => setLocation('/profile?section=comments')}
        onProfileClick={() => setLocation('/profile')}
        onLogoClick={() => setActiveTab('feed')}
      />

      <AuthPromptBanner />
      
      {showPortalBanner && (
        <div className="border-b border-border bg-muted/50 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              {showPartner && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setLocation('/partner')}
                >
                  <Store className="w-4 h-4" />
                  <span>Switch to Partner Portal</span>
                  <ArrowRight className="w-3 h-3" />
                </Button>
              )}
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setLocation('/admin')}
                >
                  <Shield className="w-4 h-4" />
                  <span>Switch to Admin Portal</span>
                  <ArrowRight className="w-3 h-3" />
                </Button>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={dismissPortalBanner}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      <main className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-hidden lg:grid lg:grid-cols-[1fr_320px]">
          <div className="flex flex-col overflow-hidden min-w-0 h-full">
            {renderTabContent()}
          </div>
          {activeTab === 'feed' && (
            <aside className="hidden lg:flex flex-col border-l border-border overflow-y-auto">
              <SectionErrorBoundary section="TrendingPanel">
                <TrendingPanel onSelect={(name) => { setWidgetFilter(name); setActiveTab('feed'); }} />
              </SectionErrorBoundary>
            </aside>
          )}
        </div>
      </main>
      
      <CreateReviewModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onReviewSubmitted={handleReviewSubmitted}
        detectedLocation={locationPromptData?.restaurant?.address}
        orderData={selectedOrderForReview ? {
          id: selectedOrderForReview.id.toString(),
          // Use eatzyClientId to resolve the local restaurant UUID via backend lookup
          // API returns restaurant.profile as the client ID (not restaurant.client)
          eatzyClientId: selectedOrderForReview.restaurant.profile || selectedOrderForReview.restaurant.client,
          // Pass the numeric Eatzy restaurant ID (e.g., 462076) for fallback resolution
          restaurantId: selectedOrderForReview.restaurant.id?.toString(),
          restaurantName: selectedOrderForReview.restaurant.name,
          restaurantAddress: selectedOrderForReview.address 
            ? `${selectedOrderForReview.address.address || selectedOrderForReview.address.street || ''}, ${selectedOrderForReview.address.city || ''}, ${selectedOrderForReview.address.state || ''} ${selectedOrderForReview.address.zipcode || selectedOrderForReview.address.zip || ''}`.trim()
            : undefined,
          // API returns "type" field for service type (not "mode")
          type: (selectedOrderForReview.type || selectedOrderForReview.mode) === 'delivery' ? 'delivery' : 'takeout',
          orderDate: new Date(selectedOrderForReview.date * 1000).toISOString(),
          items: selectedOrderForReview.items.map(item => ({
            // Use item_id (menu item ID like "I463973") for linking reviews to menu items
            // Fall back to order item id, then name if not available
            itemId: item.item_id || item.id?.toString() || item.name,
            itemName: item.name,
            quantity: item.quantity,
            price: item.price || 0
          }))
        } : undefined}
      />

      <OrderInitiationModal
        isOpen={showOrderModal}
        onClose={() => setShowOrderModal(false)}
        onProceed={handleOrderProceed}
        defaultLocation={currentLocation}
      />

      <SectionErrorBoundary section="CameraFirstReview">
        <CameraFirstReview
          isOpen={showCameraFirstReview}
          onClose={() => setShowCameraFirstReview(false)}
          onReviewSubmitted={() => {
            setShowCameraFirstReview(false);
          }}
          detectedGPS={undefined}
        />
      </SectionErrorBoundary>
      
      {/* Location-based review prompt */}
      {showLocationPrompt && locationPromptData && (
        <LocationPrompt
          isOpen={showLocationPrompt}
          restaurant={locationPromptData.restaurant}
          timeSpent={locationPromptData.timeSpent}
          sessionId={locationPromptData.sessionId}
          onStartReview={handleStartReview}
          onDismiss={handleDismissLocationPrompt}
        />
      )}

      {/* Draft Reviews Manager */}
      <DraftReviewsManager
        isOpen={showDraftManager}
        onClose={() => setShowDraftManager(false)}
        onReviewSelected={(reviewData) => {
          console.log('Draft review selected for editing:', reviewData);
          // In a real app, this would pre-populate the CreateReviewModal
          setShowCreateModal(true);
        }}
      />

      {/* Post-Order Review Prompt */}
      {showPostOrderPrompt && postOrderData && (
        <PostOrderReviewPrompt
          isOpen={showPostOrderPrompt}
          onClose={() => {
            setShowPostOrderPrompt(false);
            setPostOrderData(null);
          }}
          orderData={postOrderData}
          onReviewSubmitted={handleReviewSubmitted}
          onSkip={() => {
            setShowPostOrderPrompt(false);
            setPostOrderData(null);
            console.log('Post-order review skipped');
          }}
        />
      )}

      {/* Comments & Messages Manager */}
      <CommentsManager
        isOpen={showCommentsManager}
        onClose={() => setShowCommentsManager(false)}
      />
    </div>
  );
}

